import React, { useState, useEffect } from "react";
import apiService from "../../services/apiService";

const HeadersTab = ({ propertyData, onTabChange }) => {
  const [categories, setCategories] = useState([]);
  const [propertyStatuses, setPropertyStatuses] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [countries, setCountries] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [agents, setAgents] = useState([]);
  // Initialize with current property data
  const [selectedAgency, setSelectedAgency] = useState(
    propertyData.agent?.agency?.name || ""
  );
  const [selectedAgent, setSelectedAgent] = useState(
    propertyData.agent?.id || ""
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCategories();
    fetchPropertyStatuses();
    fetchProvinces();
    fetchCountries();
    fetchAgencies();
    // Fetch agents for current agency on initial load
    if (propertyData.agent?.agencyName) {
      fetchAgents();
    }
  }, []);

  // Fetch agents when agency changes
  useEffect(() => {
    if (selectedAgency) {
      fetchAgents();
    }
  }, [selectedAgency]);

  const fetchCategories = async () => {
    try {
      setLoading(true);

      const data = await apiService.get('/categories');
      setCategories(data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setLoading(false);
    }
  };

  const fetchPropertyStatuses = async () => {
    try {
      setLoading(true);

      const data = await apiService.get('/property-status');
      setPropertyStatuses(data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching property statuses:", err);
      setLoading(false);
    }
  };

  const fetchProvinces = async () => {
    try {
      setLoading(true);

      const data = await apiService.get('/provinces');
      setProvinces(data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching provinces:", err);
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      setLoading(true);

      const data = await apiService.get('/countries');
      setCountries(data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching countries:", err);
      setLoading(false);
    }
  };

  const fetchAgencies = async () => {
    try {
      setLoading(true);

      const data = await apiService.get('/agencies');
      setAgencies(data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching agencies:", err);
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      setLoading(true);

      const data = await apiService.get('/users?userRoleTypeId=6');
      const filteredAgents = data.filter(
        (agent) => agent.agencyName === selectedAgency
      );
      setAgents(filteredAgents);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching agents:", err);
      setLoading(false);
    }
  };

  const handleAgencyChange = (e) => {
    const newAgency = e.target.value;
    setSelectedAgency(newAgency);
    setSelectedAgent(""); // Reset selected agent when agency changes
  };

  const handleAgentChange = (e) => {
    setSelectedAgent(e.target.value);
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();

    try {
      // Get form values
      const formData = new FormData(e.target);

      const updateData = {
        userId: 1,
        categoryId: parseInt(
          formData.get("categoryId") || propertyData.category?.id
        ),
        agentId: parseInt(selectedAgent || propertyData.agent?.id),
        sellerCompanyId: 1,
        listingTitle: formData.get("listingTitle") || propertyData.listingTitle,
        listingDescription:
          formData.get("listingDescription") || propertyData.listingDescription,
        title: formData.get("listingTitle") || propertyData.listingTitle,
        shortDescription:
          formData.get("shortDescription") || propertyData.shortDescription,
        addressLine1: formData.get("addressLine1") || propertyData.addressLine1,
        addressLine2: formData.get("addressLine2") || propertyData.addressLine2,
        addressLine3: formData.get("addressLine3") || propertyData.addressLine3,
        suburb: formData.get("suburb") || propertyData.suburb,
        nearestTown: propertyData.nearestTown || "N/A", // Default to N/A, don't get from form
        provinceId: parseInt(
          formData.get("provinceId") || propertyData.province?.id
        ),
        countryId: parseInt(
          formData.get("countryId") || propertyData.country?.id
        ),
        propertyStatusId: parseInt(
          formData.get("propertyStatusId") || propertyData.propertyStatus?.id
        ),
        isFeatured: formData.get("isFeatured") === "on",
        isListable: formData.get("isListable") === "on",
        isDiscrete: formData.get("isDiscrete") === "on",
        isActive: formData.get("isActive") === "on",
        isArchived: formData.get("isArchived") === "on",
      };

      await apiService.put(`/properties/?id=${propertyData.id}`, updateData);
      
      alert("Property updated successfully!");
      // Optionally refresh the property data or navigate to summary
      onTabChange("summary");
      
    } catch (error) {
      console.error("Error updating property:", error);
      alert("Failed to update property. Please try again.");
    }
  };

  return (
    <div className="headers-content">
      <form className="headers-form" onSubmit={handleSaveChanges}>
        <div className="form-grid">
          <div className="form-section">
            <h3>Property Information</h3>
            <div className="form-row">
              <label>Reference:</label>
              <input
                type="text"
                name="propertyReference"
                defaultValue={propertyData.propertyReference}
                className="form-input"
              />
            </div>
            <div className="form-row">
              <label>Property Name:</label>
              <input
                type="text"
                name="listingTitle"
                defaultValue={propertyData.listingTitle}
                className="form-input"
              />
            </div>
            <div className="form-row">
              <label>Category:</label>
              {categories.length === 0 ? (
                <div className="loading-text">Loading categories...</div>
              ) : (
                <select
                  name="categoryId"
                  defaultValue={categories.find(c => c.title === propertyData.category)?.id || ""}
                  className="form-select"
                >
                  <option value="">Select Category...</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.title}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="form-row">
              <label>Status:</label>
              {propertyStatuses.length === 0 ? (
                <div className="loading-text">Loading statuses...</div>
              ) : (
                <select
                  name="propertyStatusId"
                  defaultValue={propertyStatuses.find(s => s.title === propertyData.propertyStatus)?.id || ""}
                  className="form-select"
                >
                  <option value="">Select Status...</option>
                  {propertyStatuses.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.title}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="form-row">
              <label>Teaser:</label>
              <textarea
                name="listingDescription"
                defaultValue={propertyData.listingDescription}
                className="form-textarea"
                rows="6"
              />
            </div>
            <div className="form-row">
              <label>Introduction:</label>
              <textarea
                name="shortDescription"
                defaultValue={propertyData.shortDescription}
                className="form-textarea"
                rows="4"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Location</h3>
            <div className="form-row">
              <label>Address Line 1:</label>
              <input
                type="text"
                name="addressLine1"
                defaultValue={propertyData.addressLine1}
                className="form-input"
              />
            </div>
            <div className="form-row">
              <label>Address Line 2:</label>
              <input
                type="text"
                name="addressLine2"
                defaultValue={propertyData.addressLine2 || ""}
                className="form-input"
              />
            </div>
            <div className="form-row">
              <label>Suburb:</label>
              <input
                type="text"
                name="suburb"
                defaultValue={propertyData.suburb}
                className="form-input"
              />
            </div>
            <div className="form-row">
              <label>Province:</label>
              {provinces.length === 0 ? (
                <div className="loading-text">Loading provinces...</div>
              ) : (
                <select
                  name="provinceId"
                  defaultValue={provinces.find(p => p.title === propertyData.province)?.id || ""}
                  className="form-select"
                >
                  <option value="">Select Province...</option>
                  {provinces.map((province) => (
                    <option key={province.id} value={province.id}>
                      {province.title}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="form-row">
              <label>Country:</label>
              {countries.length === 0 ? (
                <div className="loading-text">Loading countries...</div>
              ) : (
                <select
                  name="countryId"
                  defaultValue={countries.find(c => c.title === propertyData.country)?.id || ""}
                  className="form-select"
                >
                  <option value="">Select Country...</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.title}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3>Agent Details</h3>

            {/* 1. Agency Dropdown - populate from endpoint, show chosen from property */}
            <div className="form-row">
              <label>Agency:</label>
              {agencies.length === 0 ? (
                <div className="loading-text">Loading agencies...</div>
              ) : (
                <select
                  value={selectedAgency}
                  onChange={handleAgencyChange}
                  className="form-select"
                >
                  {agencies.map((agency) => (
                    <option key={agency.id} value={agency.agencyName}>
                      {agency.agencyName}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* 2. Agent Dropdown - populate from endpoint, show chosen from property */}
            <div className="form-row">
              <label>Agent:</label>
              {agents.length === 0 ? (
                <div className="loading-text">Loading agents...</div>
              ) : (
                <select
                  value={selectedAgent}
                  onChange={handleAgentChange}
                  className="form-select"
                >
                  <option value="">Select an agent</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.firstName} {agent.lastName}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* 3. Agent Details - show from property endpoint, hide when new agency selected */}
            {selectedAgency === propertyData.agent?.agency?.name &&
              propertyData.agent && (
                <>
                  <div className="form-row">
                    <label>First Name:</label>
                    <div className="agent-info-display">
                      {propertyData.agent.firstName}
                    </div>
                  </div>
                  <div className="form-row">
                    <label>Last Name:</label>
                    <div className="agent-info-display">
                      {propertyData.agent.lastName}
                    </div>
                  </div>
                  <div className="form-row">
                    <label>Email:</label>
                    <div className="agent-info-display">
                      {propertyData.agent.emailAddress}
                    </div>
                  </div>
                  <div className="form-row">
                    <label>Phone:</label>
                    <div className="agent-info-display">
                      {propertyData.agent.cellNumber}
                    </div>
                  </div>
                </>
              )}
          </div>

          <div className="form-section">
            <h3>Property Flags</h3>
            <div className="form-row">
              {/* <label>Carousel Featured:</label> */}
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  name="isFeatured"
                  defaultChecked={propertyData.isFeatured}
                  className="form-checkbox"
                />
                <span>Carousel Featured</span>
              </div>
            </div>
            <div className="form-row">
              {/* <label>Public Listing:</label> */}
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  name="isListable"
                  defaultChecked={propertyData.isListable}
                  className="form-checkbox"
                />
                <span>Public Listing</span>
              </div>
            </div>
            <div className="form-row">
              {/* <label>Discrete List:</label> */}
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  name="isDiscrete"
                  defaultChecked={propertyData.isDiscrete}
                  className="form-checkbox"
                />
                <span>Discrete List</span>
              </div>
            </div>
            <div className="form-row">
              {/* <label>Published:</label> */}
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  name="isActive"
                  defaultChecked={propertyData.isActive}
                  className="form-checkbox"
                />
                <span>Published</span>
              </div>
            </div>
            <div className="form-row">
              {/* <label>Archived:</label> */}
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  name="isArchived"
                  defaultChecked={propertyData.isArchived}
                  className="form-checkbox"
                />
                <span>Archived</span>
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary">
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default HeadersTab;
